import { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import {
  SR_COMMITMENT,
  SR_CURRICULUM_THEMES,
  SR_DIVERSITY_DIMENSIONS,
  SR_DIVERSITY_INTRO,
  SR_DIVERSITY_PILLARS,
  SR_PRINCIPLES,
  SR_STAKEHOLDERS,
} from '@/data/about';
import { cn } from '@ibas/ui';

/**
 * SocialResponsibility (/about — Section 7 ★ accreditation-mandated, titled
 * exactly "Social Responsibility"). A large, well-structured section that renders
 * Appendix E VERBATIM in five movements: the commitment statement, the three
 * structuring principles, the five responsible-education curriculum themes, an
 * interactive tabbed stakeholder matrix (five groups, each with what they expect
 * and how IBAS acts), and the diversity & inclusion block (intro, dimension chips
 * and pillar cards).
 *
 * Motion: SplitReveal headline, ScrollReveal child-staggers for every card grid,
 * and — for the stakeholder tabs — a Framer cross-fade between panels that is
 * disabled under reduced motion (plain conditional render) so content is never
 * stranded hidden. The tablist is keyboard-navigable (roving tabindex, Arrow /
 * Home / End). Every institutional string is imported verbatim from SR_* in
 * '@/data/about'; only framing labels and aria descriptions are local microcopy.
 */
export function SocialResponsibility() {
  const [activeGroup, setActiveGroup] = useState(0);
  const reduced = useReducedMotion();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [lede, ...restCommitment] = SR_COMMITMENT;
  const active = SR_STAKEHOLDERS[activeGroup];

  const focusTab = (index: number) => {
    const next = (index + SR_STAKEHOLDERS.length) % SR_STAKEHOLDERS.length;
    setActiveGroup(next);
    tabRefs.current[next]?.focus();
  };

  const handleTabKey = (event: React.KeyboardEvent, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        focusTab(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        focusTab(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusTab(0);
        break;
      case 'End':
        event.preventDefault();
        focusTab(SR_STAKEHOLDERS.length - 1);
        break;
      default:
        break;
    }
  };

  const panelBody = (
    <>
      <p className="max-w-3xl text-base leading-relaxed text-muted-foreground">{active.intro}</p>
      <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <p className="font-label text-xs tracking-[0.18em] text-brand-gold uppercase">
            What they expect
          </p>
          <ul className="mt-4 space-y-3">
            {active.expects.map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-foreground">
                <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-gold" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="font-label text-xs tracking-[0.18em] text-brand-navy uppercase">
            How we act responsibly
          </p>
          <ul className="mt-4 space-y-3">
            {active.acts.map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-foreground">
                <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-navy" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );

  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── a) Commitment ────────────────────────────────────────────────── */}
        <header className="max-w-3xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Social responsibility
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Our social responsibility.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">{lede}</p>
          </ScrollReveal>
          {restCommitment.length > 0 && (
            <ScrollReveal from="up" stagger={0.08} className="mt-4 space-y-4">
              {restCommitment.map((paragraph) => (
                <p key={paragraph} className="text-base leading-relaxed text-muted-foreground">
                  {paragraph}
                </p>
              ))}
            </ScrollReveal>
          )}
        </header>

        {/* ── b) Principles ────────────────────────────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Our principles
          </p>
          <ScrollReveal stagger={0.1} className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {SR_PRINCIPLES.map((principle, index) => (
              <div
                key={principle}
                className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <span
                  aria-hidden
                  className="font-display text-2xl font-semibold text-brand-gold tabular-nums"
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="mt-4 font-display text-lg leading-snug font-semibold text-foreground">
                  {principle}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>

        {/* ── c) Responsible education & curriculum design ─────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Responsible education &amp; curriculum design
          </p>
          <ScrollReveal
            stagger={0.1}
            className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {SR_CURRICULUM_THEMES.map((theme) => (
              <div
                key={theme.title}
                className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                  <theme.Icon aria-hidden className="size-6" />
                </div>
                <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                  {theme.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{theme.body}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>

        {/* ── d) Responsibility to our stakeholders — tabbed matrix ────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Our responsibility to our stakeholders
          </p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Select a group to see what it expects of IBAS and how we act responsibly in response.
          </p>

          <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
            {/* ── Tablist ──────────────────────────────────────────────────── */}
            <div
              role="tablist"
              aria-label="IBAS stakeholder groups"
              aria-orientation="horizontal"
              className="flex flex-wrap gap-2 border-b border-border bg-muted/30 p-3"
            >
              {SR_STAKEHOLDERS.map((stakeholder, index) => {
                const selected = index === activeGroup;
                return (
                  <button
                    key={stakeholder.group}
                    ref={(node) => {
                      tabRefs.current[index] = node;
                    }}
                    type="button"
                    role="tab"
                    id={`sr-stakeholder-tab-${index}`}
                    aria-selected={selected}
                    aria-controls="sr-stakeholder-panel"
                    tabIndex={selected ? 0 : -1}
                    onClick={() => setActiveGroup(index)}
                    onKeyDown={(event) => handleTabKey(event, index)}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold motion-reduce:transition-none',
                      selected
                        ? 'bg-brand-navy text-white'
                        : 'text-muted-foreground hover:bg-brand-navy/5 hover:text-brand-navy',
                    )}
                  >
                    <stakeholder.Icon aria-hidden className="size-4" />
                    {stakeholder.group}
                  </button>
                );
              })}
            </div>

            {/* ── Panel ────────────────────────────────────────────────────── */}
            <div
              role="tabpanel"
              id="sr-stakeholder-panel"
              aria-labelledby={`sr-stakeholder-tab-${activeGroup}`}
              className="p-7 md:p-9"
            >
              {reduced ? (
                panelBody
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active.group}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {panelBody}
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>

        {/* ── e) Diversity & inclusive academic culture ────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Diversity &amp; inclusive academic culture
          </p>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">
              {SR_DIVERSITY_INTRO}
            </p>
          </ScrollReveal>

          {/* ── Dimension chips ─────────────────────────────────────────────── */}
          <ScrollReveal as="ul" from="up" stagger={0.06} className="mt-8 flex flex-wrap gap-3">
            {SR_DIVERSITY_DIMENSIONS.map((dimension) => (
              <li
                key={dimension}
                className="rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-brand-navy"
              >
                {dimension}
              </li>
            ))}
          </ScrollReveal>

          {/* ── Pillar cards ────────────────────────────────────────────────── */}
          <ScrollReveal stagger={0.1} className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {SR_DIVERSITY_PILLARS.map((pillar) => (
              <div
                key={pillar.title}
                className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                  <pillar.Icon aria-hidden className="size-6" />
                </div>
                <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                  {pillar.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{pillar.body}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
